import { ArrowUp, Phone } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';

export function MioComposer({
  onSend,
  onCall,
  disabled,
}: {
  onSend: (text: string) => void;
  onCall?: () => void;
  disabled: boolean;
}) {
  const [text, setText] = useState('');
  const field = useRef<HTMLTextAreaElement>(null);

  // Grow with the message, up to a limit.
  useEffect(() => {
    const el = field.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!text.trim() || disabled) return;
    onSend(text);
    setText('');
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.nativeEvent.isComposing) return;
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  }

  return (
    <form
      onSubmit={submit}
      className="border-line-strong bg-canvas focus-within:border-ring focus-within:ring-ring/15 rounded-md border p-3 transition-colors focus-within:ring-2"
    >
      <label htmlFor="mio-composer" className="sr-only">
        Message Mio
      </label>
      <textarea
        id="mio-composer"
        ref={field}
        rows={2}
        maxLength={12000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Ask Mio a question…"
        className="text-fg placeholder:text-fg-subtle min-h-12 w-full min-w-0 resize-none bg-transparent text-base leading-6 outline-none lg:text-[15px]"
      />
      <div className="mt-2 flex items-center justify-between gap-3">
        {onCall ? (
          <button
            type="button"
            onClick={onCall}
            disabled={disabled}
            aria-label="Call Mio"
            title="Call Mio"
            className="text-fg-muted hover:text-fg hover:bg-surface inline-flex min-h-9 items-center gap-2 rounded px-2 text-sm transition-colors disabled:opacity-40"
          >
            <Phone className="size-4" aria-hidden="true" />
            Voice
          </button>
        ) : (
          <span className="text-fg-subtle text-xs">Shift + Enter for a new line</span>
        )}
        <button
          type="submit"
          disabled={disabled || !text.trim()}
          aria-label="Send message"
          className="bg-primary text-primary-fg hover:bg-primary-hover inline-flex min-h-9 shrink-0 items-center justify-center gap-2 rounded px-3 text-sm font-semibold transition-colors disabled:opacity-30"
        >
          Send
          <ArrowUp className="size-4" aria-hidden="true" />
        </button>
      </div>
    </form>
  );
}
